// «Nombres parecidos» del Explorador (2026-10-02, docs/sessions/2026-10-02-buscador-parecidos.md): cuando la búsqueda por
// nombre no encuentra a nadie, la base (public.nexo_viz_buscar_parecidos → nexo.buscar_parecidos) propone hasta cinco nombres
// parecidos. Aquí sólo vive lo que decide la pantalla, sin React, para probarlo con Deno (app/scripts/nexo_parecidos_test.ts).
import type { Ruta } from "./ruta.ts";

/** Una fila de nexo.buscar_parecidos: la forma de fila del buscador, más dónde está el nombre. `parecido` ordena; no se muestra. */
export interface NombreParecido {
  kind: "entity" | "siger_actor";
  id: string;
  runId: string | null;
  caption: string;
  schema: string;
  pais: string | null;
  /** El otro nombre de la lista que se parece, cuando no es el principal. */
  nombreParecido: string | null;
  /** Dónde está el nombre: lista de OFAC, lista del SAT, ya salió de la lista de OFAC o el Registro Público de Comercio. No es una
   * relación con OFAC. */
  lista: "ofac" | "sat" | "ofac_levantada" | "registro" | null;
  /** La ficha que abre una persona o empresa del registro (la de su propia identidad o la del sancionado con quien se relaciona). */
  fichaEntidad: string | null;
  parecido: number;
}

/** Lo que la pantalla sabe en el momento de decidir si pide nombres parecidos. */
export interface EstadoBusqueda {
  /** La consulta escrita, sin espacios a los lados. */
  q: string;
  /** ¿Hay otro filtro además del nombre (tipo, país, programa, fuente, registro)? */
  otrosFiltros: boolean;
  pagina: number;
  /** La consulta cuya respuesta de la lista ya llegó, y cuántos encontró. */
  listaDe: string | null;
  listaTotal: number | null;
  /** La consulta cuya respuesta del registro ya llegó (sin error), y cuántos encontró. */
  registroDe: string | null;
  registroTotal: number;
}

/**
 * Se piden nombres parecidos sólo si la búsqueda por nombre ya respondió vacía en la lista y en el registro, para esa misma
 * consulta, en la primera página y sin otros filtros (un filtro que deja la tabla vacía no es un nombre mal escrito).
 */
export function pedirParecidos(e: EstadoBusqueda): boolean {
  return e.q.length >= 3 && e.q.length <= 160 && !e.otrosFiltros && e.pagina === 0
    && e.listaDe === e.q && e.listaTotal === 0 && e.registroDe === e.q && e.registroTotal === 0;
}

/**
 * A dónde lleva cada nombre parecido: como un resultado normal. Una entidad, a su ficha; una persona o empresa del registro, a su
 * propia ficha (etapa 4: nexo.hallazgos en su corrida, como el nombre de una fila del buscador). Sin corrida, a la ficha que propone
 * la base (`fichaEntidad`) con la persona elegida, como antes.
 */
export function fichaDeParecido(p: NombreParecido): Ruta | null {
  if (p.kind === "entity") return { vista: "entidad", id: p.id };
  if (p.runId) return { vista: "registro", id: p.id, runId: p.runId };
  return p.fichaEntidad ? { vista: "entidad", id: p.fichaEntidad, actor: p.id } : null;
}

const DONDE: Record<NonNullable<NombreParecido["lista"]>, string> = {
  ofac: "en la lista de OFAC",
  sat: "en la lista del SAT",
  ofac_levantada: "ya salió de la lista de OFAC",
  registro: "en el Registro Público de Comercio",
};

/** La línea gris bajo el nombre: tipo, dónde está y el otro nombre que se parece. Nunca «coincide» ni un porcentaje. */
export function detalleDeParecido(p: NombreParecido): string {
  const tipo = p.schema === "Person" ? "persona" : p.schema === "Company" || p.schema === "Organization" ? "empresa" : "";
  return [tipo, p.lista ? DONDE[p.lista] : "", p.nombreParecido ? `también aparece como «${p.nombreParecido}»` : ""]
    .filter(Boolean)
    .join(" · ");
}
